/**
 * Session filter for Total Number Of Contracts / revenue payment logs.
 * Angular key: `paymentlogs_filter` (`ReportsTotalNumberContractComponent`).
 */

import { dmyToYmd } from './reportDateParams'
import type { TotalContractsListPatch } from '../types/utils'
import { PAYMENT_LOGS_SESSION_KEY } from '../constants/sessionKeys'

export { PAYMENT_LOGS_SESSION_KEY }

export function readPaymentLogsSession(): Record<string, unknown> | null {
  try {
    const raw = sessionStorage.getItem(PAYMENT_LOGS_SESSION_KEY)
    if (!raw) return null
    const v = JSON.parse(raw) as unknown
    return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null
  } catch {
    return null
  }
}

export function writePaymentLogsSession(data: Record<string, unknown>): void {
  sessionStorage.setItem(PAYMENT_LOGS_SESSION_KEY, JSON.stringify(data))
}

export function clearPaymentLogsSession(): void {
  sessionStorage.removeItem(PAYMENT_LOGS_SESSION_KEY)
}

function normalizeStoredDate(value: unknown): string {
  if (value == null) return ''
  const s = String(value).trim()
  if (!s) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  const dmy = dmyToYmd(s)
  return dmy || s
}

function pickStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String).filter(Boolean)
  if (typeof value === 'string' && value.trim()) return [value.trim()]
  return []
}

export function sessionFilterToTotalContractsPatch(session: Record<string, unknown>): {
  patch: TotalContractsListPatch
  propertyNameHint: string
  companyNameHint: string
} {
  const patch: TotalContractsListPatch = {}

  if (typeof session.page === 'number' && session.page > 0) patch.page = session.page
  if (typeof session.perPage === 'number' && session.perPage > 0) patch.perPage = session.perPage
  if (session.sortBy === 'asc' || session.sortBy === 'desc') patch.sortBy = session.sortBy
  if (typeof session.orderBy === 'string' && session.orderBy.trim()) patch.orderBy = session.orderBy
  if (typeof session.q === 'string') patch.q = session.q

  if (session.company_id != null && session.company_id !== '') {
    patch.company_id = String(session.company_id)
  }
  if (session.property_id != null && session.property_id !== '') {
    patch.property_id = String(session.property_id)
  }

  const fromDt = normalizeStoredDate(session.fromDt)
  const toDt = normalizeStoredDate(session.toDt)
  if (fromDt) patch.fromDt = fromDt
  if (toDt) patch.toDt = toDt

  const csFrom = normalizeStoredDate(session.contract_start_fromDt)
  const csTo = normalizeStoredDate(session.contract_start_toDt)
  if (csFrom) patch.contract_start_fromDt = csFrom
  if (csTo) patch.contract_start_toDt = csTo

  const fundingFrom = normalizeStoredDate(session.funding_fromDt)
  const fundingTo = normalizeStoredDate(session.funding_toDt)
  if (fundingFrom) patch.funding_fromDt = fundingFrom
  if (fundingTo) patch.funding_toDt = fundingTo

  const types = pickStringArray(session.type ?? session['type[]'])
  if (types.length) patch.type = types

  if (typeof session.source === 'string') patch.source = session.source
  const statuses = pickStringArray(session.status ?? session['status[]'])
  if (statuses.length) patch.status = statuses
  const gateways = pickStringArray(session.gateway_status ?? session['gateway_status[]'])
  if (gateways.length) patch.gateway_status = gateways
  if (typeof session.special_note === 'string') patch.special_note = session.special_note

  const propertyNameHint =
    typeof session.property_name === 'string' ? session.property_name : ''
  const companyNameHint =
    typeof session.company_name === 'string' ? session.company_name : ''

  return { patch, propertyNameHint, companyNameHint }
}

function ymdToDmy(ymd: string): string {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ''
  const [y, m, d] = ymd.split('-')
  return `${d}-${m}-${y}`
}

export function buildPaymentLogsSessionFromParams(p: {
  page: number
  perPage: number
  sortBy: string
  orderBy: string
  q: string
  company_id: string
  property_id: string
  fromDt: string
  toDt: string
  contract_start_fromDt: string
  contract_start_toDt: string
  funding_fromDt?: string
  funding_toDt?: string
  type: string[]
  source: string
  status: string[]
  gateway_status: string[]
  special_note: string
}): Record<string, unknown> {
  const out: Record<string, unknown> = {
    page: p.page,
    perPage: p.perPage,
    sortBy: p.sortBy,
    orderBy: p.orderBy,
    q: p.q,
  }
  if (p.company_id) out.company_id = p.company_id
  if (p.property_id) out.property_id = p.property_id
  const fd = ymdToDmy(p.fromDt)
  const td = ymdToDmy(p.toDt)
  if (fd) out.fromDt = fd
  if (td) out.toDt = td
  const csf = ymdToDmy(p.contract_start_fromDt)
  const cst = ymdToDmy(p.contract_start_toDt)
  if (csf) out.contract_start_fromDt = csf
  if (cst) out.contract_start_toDt = cst
  const ff = ymdToDmy(p.funding_fromDt ?? '')
  const ft = ymdToDmy(p.funding_toDt ?? '')
  if (ff) out.funding_fromDt = ff
  if (ft) out.funding_toDt = ft
  if (p.type.length) out.type = [...p.type]
  if (p.source.trim()) out.source = p.source.trim()
  if (p.status.length) out.status = [...p.status]
  if (p.gateway_status.length) out.gateway_status = [...p.gateway_status]
  if (p.special_note.trim()) out.special_note = p.special_note.trim()
  return out
}
